import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { closestCenter, DndContext, DragEndEvent, DragOverlay, DragStartEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { SortableContext, horizontalListSortingStrategy } from '@dnd-kit/sortable'
import { Checkbox } from './Checkbox'
import { Skeleton } from './Skeleton'
import { ColumnDragGhost } from './table/ColumnDragGhost'
import { SortableHeaderCell } from './table/SortableHeaderCell'
import { TableColumnsMenu } from './table/TableColumnsMenu'
import { useColumnLayout } from './table/useColumnLayout'
import { useColumnResize } from './table/useColumnResize'

export interface DataTableColumn<Key extends string> {
    key: Key
    label: string
    width: number
    minWidth: number
    align?: 'left' | 'center' | 'right'
    hideable?: boolean
    /** Hidden until the viewer turns it on in column customization; applies only while they have no saved choice. */
    defaultHidden?: boolean
    collapsible?: boolean
    draggable?: boolean
}

interface DataTableProps<Row, Key extends string> {
    columns: Array<DataTableColumn<Key>>
    rows: Row[]
    rowKey: (row: Row) => string
    storageKey: string
    loading?: boolean
    error?: string
    emptyMessage: string
    ariaLabel: string
    loadingRowCount?: number
    onRowClick?: (row: Row) => void
    renderCell: (key: Key, row: Row) => React.ReactNode
    /**
     * Opt-in row selection: a leading checkbox column, and a header checkbox for the rows on screen.
     * The caller owns the selection, so it can outlive a page of rows; tables that leave this out
     * render exactly as before.
     */
    selection?: DataTableSelection<Row>
    /**
     * Take the height the parent gives it (the parent must be a flex column) and scroll the rows and
     * columns inside the table, with the header row pinned — so what follows the table, like its
     * pagination, stays in view instead of below the fold. The parent sets the height and any minimum.
     * Tables that leave this out size to their rows, exactly as before.
     */
    fillHeight?: boolean
    /** Draw a light border between cells, header and body alike; tables that leave this out keep row dividers only. */
    bordered?: boolean
    /** Tighter cells (less padding around each value) for dense tables; tables that leave this out keep the roomier default. */
    compact?: boolean
    /** The ⋮ menu on each header (collapse, hide). Pass `false` for a table whose columns are managed from the Columns button alone. */
    headerMenu?: boolean
    /** A column pinned to the right edge, so it stays in view while the other columns scroll sideways (a row's actions menu). */
    stickyEndKey?: Key
    /**
     * Show the Columns button in a toolbar the caller owns instead of in the strip above the header. Leave it out for the
     * built-in strip. Pass the element the button goes into; `null` is a host that has not mounted yet, so nothing shows
     * (neither strip nor button) until it does. The hidden columns stay the table's own.
     */
    columnsMenuHost?: HTMLElement | null
}

export interface DataTableSelection<Row> {
    isSelected: (row: Row) => boolean
    onToggleRow: (row: Row) => void
    /** Select or clear every row currently shown. */
    onToggleRows: (rows: Row[], selected: boolean) => void
    /** Accessible name of a row's checkbox. */
    rowLabel: (row: Row) => string
}

/** Width of the selection column; reserved from the space the data columns share. */
const SELECTION_COLUMN_WIDTH = 48

/**
 * Columns the viewer chose to hide. Hidden keys are stored rather than visible ones so a column
 * added (or renamed) later shows by default: a stored visible list cannot tell "hidden by the user"
 * from "did not exist yet", and silently hid every new column for anyone with saved preferences.
 */
const readHiddenColumns = <Key extends string>(storageKey: string, columns: Array<DataTableColumn<Key>>): Key[] => {
    const defaults = columns.filter((column) => column.hideable !== false && column.defaultHidden).map((column) => column.key)
    if (typeof window === 'undefined') return defaults
    try {
        const parsed: unknown = JSON.parse(window.localStorage.getItem(`${storageKey}:hidden`) ?? 'null')
        if (!Array.isArray(parsed)) return defaults
        return columns.filter((column) => column.hideable !== false && parsed.some((stored) => stored === column.key)).map((column) => column.key)
    } catch {
        return defaults
    }
}

/**
 * A pinned cell needs an opaque background so scrolled cells pass beneath it, and its own left edge: collapsed table borders stay
 * behind with the grid on scroll, so the divider is drawn by the cell and travels with it.
 */
const PINNED_END_CELL_CLASS =
    'sticky right-0 z-[1] bg-surface group-hover:bg-surface-secondary before:pointer-events-none before:absolute before:inset-y-0 before:left-0 before:w-px before:bg-border'

const alignmentClass = (align?: 'left' | 'center' | 'right'): string => {
    if (align === 'right') return 'text-right'
    if (align === 'center') return 'text-center'
    return 'text-left'
}

/**
 * The hidden columns, order and widths are read from `storageKey` once, when the table mounts. A table whose key
 * changes while it stays mounted (one table serving two tabs) would otherwise keep the previous key's layout and
 * write it over the new key's, so a new key mounts a fresh table that reads its own.
 */
export const DataTable = <Row, Key extends string>(props: DataTableProps<Row, Key>) => <StoredDataTable<Row, Key> key={props.storageKey} {...props} />

const StoredDataTable = <Row, Key extends string>({
    columns,
    rows,
    rowKey,
    storageKey,
    loading = false,
    error,
    emptyMessage,
    ariaLabel,
    loadingRowCount = 6,
    onRowClick,
    renderCell,
    selection,
    fillHeight = false,
    bordered = false,
    compact = false,
    headerMenu = true,
    stickyEndKey,
    columnsMenuHost,
}: DataTableProps<Row, Key>) => {
    const allKeys = useMemo(() => columns.map(({ key }) => key), [columns])
    const defaultWidths = useMemo(() => Object.fromEntries(columns.map(({ key, width }) => [key, width])), [columns])
    const minimumWidths = useMemo(() => Object.fromEntries(columns.map(({ key, minWidth }) => [key, minWidth])), [columns])
    const [hiddenKeys, setHiddenKeys] = useState<Key[]>(() => readHiddenColumns(storageKey, columns))
    const visibleKeys = useMemo(() => {
        const hidden = new Set(hiddenKeys)
        const visible = columns.filter((column) => column.hideable === false || !hidden.has(column.key)).map((column) => column.key)
        return visible.length > 0 ? visible : allKeys
    }, [allKeys, columns, hiddenKeys])
    const {
        state: { order, isCollapsed },
        dimensions: { getWidth, getMinWidth },
        actions: { reorderColumn, resizeColumn, toggleCollapsed },
    } = useColumnLayout(allKeys, { storageKey, defaultWidths, minWidth: minimumWidths })
    const orderedVisibleKeys = useMemo(() => {
        const visible = new Set(visibleKeys)
        // A column the saved order has not seen yet goes at the end rather than disappearing.
        const completeOrder = [...order.filter((key) => allKeys.includes(key)), ...allKeys.filter((key) => !order.includes(key))]
        return completeOrder.filter((key) => visible.has(key))
    }, [allKeys, order, visibleKeys])
    const visibleColumns = useMemo(
        () => orderedVisibleKeys.map((key) => columns.find((column) => column.key === key)).filter((column) => column !== undefined),
        [columns, orderedVisibleKeys]
    )
    const { scrollContainerRef, effectiveWidth, handleResizeStart, activeResizeKey } = useColumnResize({
        visibleKeys: orderedVisibleKeys,
        getWidth,
        getMinWidth,
        isCollapsed,
        resizeColumn,
        fixedColumnsWidth: selection ? SELECTION_COLUMN_WIDTH : 0,
    })
    const selectedOnScreen = selection ? rows.filter((row) => selection.isSelected(row)).length : 0
    const allOnScreenSelected = rows.length > 0 && selectedOnScreen === rows.length
    const columnCount = visibleColumns.length + (selection ? 1 : 0)
    const cellBorder = bordered ? ' border-r border-border last:border-r-0' : ''
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }))
    const [activeDragKey, setActiveDragKey] = useState<Key | null>(null)

    useEffect(() => {
        // A convenience, like the column layout: unavailable storage must never take the page down.
        try {
            window.localStorage.setItem(`${storageKey}:hidden`, JSON.stringify(hiddenKeys))
            // The old visible-list format could hide new columns; drop it so it is never read again.
            window.localStorage.removeItem(`${storageKey}:visible`)
        } catch {
            // hidden columns simply are not remembered
        }
    }, [storageKey, hiddenKeys])

    const setVisibleColumns = (next: Key[]) => {
        const nextSet = new Set(next)
        setHiddenKeys(columns.filter((column) => column.hideable !== false && !nextSet.has(column.key)).map((column) => column.key))
    }
    const hideColumn = (key: Key) => setVisibleColumns(visibleKeys.filter((visibleKey) => visibleKey !== key))
    const handleDragEnd = (event: DragEndEvent) => {
        setActiveDragKey(null)
        const overKey = event.over?.id
        if (typeof overKey === 'string' && event.active.id !== overKey) {
            const activeKey = allKeys.find((key) => key === event.active.id)
            const targetKey = allKeys.find((key) => key === overKey)
            if (activeKey && targetKey) reorderColumn(activeKey, targetKey)
        }
    }

    const columnsMenu = (
        <TableColumnsMenu
            columns={columns.map(({ key, label, hideable }) => ({ key, label, locked: hideable === false }))}
            visibleColumns={visibleKeys}
            allKeys={allKeys}
            onChange={setVisibleColumns}
            size={columnsMenuHost === undefined ? 'sm' : 'md'}
        />
    )

    const cellPadding = compact ? 'px-3 py-1.5' : 'px-4 py-3'

    return (
        <div aria-busy={loading} className={fillHeight ? 'flex min-h-0 flex-1 flex-col' : undefined}>
            {columnsMenuHost === undefined && (
                <div className="flex min-h-12 shrink-0 items-center justify-end border-y border-border bg-surface px-4 py-2">{columnsMenu}</div>
            )}
            {columnsMenuHost && createPortal(columnsMenu, columnsMenuHost)}
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={(event: DragStartEvent) => {
                    const key = allKeys.find((candidate) => candidate === event.active.id)
                    if (key) setActiveDragKey(key)
                }}
                onDragEnd={handleDragEnd}
                onDragCancel={() => setActiveDragKey(null)}
            >
                {/* pb-2: breathing room above the horizontal scrollbar — without it, the scrollbar
                    sits flush against the table's last row and whatever follows (e.g. a bordered
                    pagination bar), reading as the table being glued to the bottom of its card. */}
                <div ref={scrollContainerRef} className={fillHeight ? 'min-h-0 flex-1 overflow-auto pb-2' : 'overflow-x-auto pb-2'}>
                    <table aria-label={ariaLabel} className="w-full table-fixed text-xs">
                        <colgroup>
                            {selection && <col style={{ width: SELECTION_COLUMN_WIDTH }} />}
                            {visibleColumns.map((column) => (
                                <col key={column.key} style={{ width: effectiveWidth[column.key] }} />
                            ))}
                        </colgroup>
                        {/* Pinned while the rows scroll inside a filled-height table; above the cells, below popovers. */}
                        <thead className={fillHeight ? 'sticky top-0 z-[2]' : undefined}>
                            <tr className="border-b border-border bg-surface">
                                {selection && (
                                    <th scope="col" className={`${cellPadding}${cellBorder}`}>
                                        <Checkbox
                                            ariaLabel="Select all rows on this page"
                                            checked={allOnScreenSelected}
                                            indeterminate={selectedOnScreen > 0 && !allOnScreenSelected}
                                            disabled={loading || rows.length === 0}
                                            onChange={() => selection.onToggleRows(rows, !allOnScreenSelected)}
                                        />
                                    </th>
                                )}
                                <SortableContext items={orderedVisibleKeys} strategy={horizontalListSortingStrategy}>
                                    {visibleColumns.map((column) => (
                                        <SortableHeaderCell
                                            key={column.key}
                                            columnKey={column.key}
                                            label={column.label}
                                            align={column.align}
                                            draggable={column.draggable !== false}
                                            collapsible={headerMenu && column.collapsible !== false}
                                            bordered={bordered}
                                            compact={compact}
                                            pinnedEnd={column.key === stickyEndKey}
                                            onResizeStart={handleResizeStart(column.key)}
                                            isResizing={activeResizeKey === column.key}
                                            collapsed={isCollapsed(column.key)}
                                            onToggleCollapsed={() => toggleCollapsed(column.key)}
                                            onHide={headerMenu ? () => hideColumn(column.key) : undefined}
                                            canHide={column.hideable !== false && visibleColumns.length > 1}
                                        />
                                    ))}
                                </SortableContext>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                Array.from({ length: loadingRowCount }, (_, rowIndex) => (
                                    <tr key={rowIndex} className="h-12 border-b border-border">
                                        {selection && <td className={`${cellPadding}${cellBorder}`} />}
                                        {visibleColumns.map((column, columnIndex) => (
                                            <td key={column.key} className={`${cellPadding}${cellBorder}`}>
                                                <Skeleton width={`${48 + ((rowIndex + columnIndex) % 5) * 9}%`} height={10} radius={8} />
                                            </td>
                                        ))}
                                    </tr>
                                ))
                            ) : error ? (
                                <tr>
                                    <td colSpan={columnCount} className="h-56 px-8 text-center text-sm text-danger">
                                        {error}
                                    </td>
                                </tr>
                            ) : rows.length === 0 ? (
                                <tr>
                                    <td colSpan={columnCount} className="h-56 px-8 text-center text-sm text-foreground-muted">
                                        {emptyMessage}
                                    </td>
                                </tr>
                            ) : (
                                rows.map((row) => (
                                    <tr
                                        key={rowKey(row)}
                                        tabIndex={onRowClick ? 0 : undefined}
                                        onClick={() => onRowClick?.(row)}
                                        onKeyDown={(event) => {
                                            if (event.target === event.currentTarget && event.key === 'Enter') onRowClick?.(row)
                                        }}
                                        className={`border-b border-border transition-colors duration-150 ${
                                            onRowClick
                                                ? 'group cursor-pointer hover:bg-surface-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent'
                                                : ''
                                        }`}
                                    >
                                        {selection && (
                                            // Ticking a row must not also open it.
                                            <td
                                                className={`${cellPadding}${cellBorder}`}
                                                onClick={(event) => event.stopPropagation()}
                                                onKeyDown={(event) => event.stopPropagation()}
                                            >
                                                <Checkbox
                                                    ariaLabel={selection.rowLabel(row)}
                                                    checked={selection.isSelected(row)}
                                                    onChange={() => selection.onToggleRow(row)}
                                                />
                                            </td>
                                        )}
                                        {visibleColumns.map((column) => (
                                            <td
                                                key={column.key}
                                                className={`overflow-hidden ${cellPadding} ${alignmentClass(column.align)}${cellBorder}${
                                                    column.key === stickyEndKey ? ` ${PINNED_END_CELL_CLASS}` : ''
                                                }`}
                                            >
                                                {!isCollapsed(column.key) && renderCell(column.key, row)}
                                            </td>
                                        ))}
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
                <DragOverlay dropAnimation={{ duration: 180, easing: 'cubic-bezier(0.2, 0, 0, 1)' }}>
                    {activeDragKey ? (
                        <ColumnDragGhost
                            label={columns.find((column) => column.key === activeDragKey)?.label ?? activeDragKey}
                            width={getWidth(activeDragKey)}
                        />
                    ) : null}
                </DragOverlay>
            </DndContext>
        </div>
    )
}
